import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProgressProvider } from './context/ProgressContext';
import { Toaster } from './components/ui/sonner';
import UpdatePrompt from './components/UpdatePrompt';
import PageTracker from './components/PageTracker';
import UserLayout from './components/UserLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { Loader2 } from 'lucide-react';
import { initAttributionTracker, adoptCampaignUtms } from './services/attribution';
import { redirectUrlWithUtms, utmsFromSearch, REDIRECT_UTMS } from './lib/ayekaOffer';
import './App.css';

// FCC Attribution — initialize at MODULE LOAD time so UTMs are captured
// before any React lazy-loading or Suspense delays. Safe to call repeatedly.
try { initAttributionTracker(); } catch { /* noop */ }

// Lazy load heavy components
const ApplicationQuestionnaire = lazy(() => import('./pages/ApplicationQuestionnaire'));
const InterviewBookingPage = lazy(() => import('./pages/InterviewBookingPage'));
const RutaCriticaApplicationPage = lazy(() => import('./pages/RutaCriticaApplicationPage'));
const Login = lazy(() => import('./pages/Login'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const AuthLink = lazy(() => import('./pages/AuthLink'));
const TrialAccess = lazy(() => import('./pages/TrialAccess'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const HomePage = lazy(() => import('./pages/HomePage'));
const PublicHomePage = lazy(() => import('./pages/PublicHomePage'));
const CoursesPage = lazy(() => import('./pages/CoursesPage'));
const PublicCoursesPage = lazy(() => import('./pages/PublicCoursesPage'));
const RutaCriticaPage = lazy(() => import('./pages/RutaCriticaPage'));
const AyekaPage = lazy(() => import('./pages/AyekaPage'));
const CirculoPage = lazy(() => import('./pages/CirculoPage'));
const NewYearCoursePage = lazy(() => import('./pages/NewYearCoursePage'));
const NewYearClassPage = lazy(() => import('./pages/NewYearClassPage'));
const NewYearExercise = lazy(() => import('./pages/NewYearExercise'));
const NewYearDonate = lazy(() => import('./pages/NewYearDonate'));
const GuestDonate = lazy(() => import('./pages/GuestDonate'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const VideosPage = lazy(() => import('./pages/VideosPage'));
const PodcastPage = lazy(() => import('./pages/PodcastPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage').then(module => ({ default: module.default })));
const PaymentSuccess = lazy(() => import('./pages/PaymentPage').then(module => ({ default: module.PaymentSuccess })));
const PaymentCancel = lazy(() => import('./pages/PaymentPage').then(module => ({ default: module.PaymentCancel })));
const WeeklyQuestionnaire = lazy(() => import('./pages/WeeklyQuestionnaire'));
const PreCourseQuestionnaire = lazy(() => import('./pages/PreCourseQuestionnaire'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const AyekaLandingPage = lazy(() => import('./pages/AyekaLandingPage'));
const AyekaMonthLanding = lazy(() => import('./pages/AyekaMonthLanding'));
const AyekaBienvenida = lazy(() => import('./pages/AyekaBienvenida'));
const AyekaTrialStart = lazy(() => import('./pages/AyekaTrialStart'));
// /ayeka-promo y /links ya no son páginas: redirect a /curso/ayeka (Jack, 25 oct 2026). También hay 301 en el Caddyfile.
const AyekaTrialWelcome = lazy(() => import('./pages/AyekaTrialWelcome'));
const AyekaConvertir = lazy(() => import('./pages/AyekaConvertir'));
const AyekaConvertido = lazy(() => import('./pages/AyekaConvertido'));
const AyekaComenzar = lazy(() => import('./pages/AyekaComenzar'));
const AyekaLesson = lazy(() => import('./pages/AyekaLesson'));
const MentalLandingPage = lazy(() => import('./pages/MentalLandingPage'));
const MediaLandingPage = lazy(() => import('./pages/MediaLandingPage'));
const RutaCriticaLandingPage = lazy(() => import('./pages/RutaCriticaLandingPage'));
const RetiroLandingPage = lazy(() => import('./pages/RetiroLandingPage'));
const RutaCriticaPaymentPage = lazy(() => import('./pages/RutaCriticaPaymentPage'));
const CirculoLandingPage = lazy(() => import('./pages/CirculoLandingPage'));
const NewYearLandingPage = lazy(() => import('./pages/NewYearLandingPage'));
const ReferralLandingPage = lazy(() => import('./pages/ReferralLandingPage'));
const PoliticaPrivacidad = lazy(() => import('./pages/PoliticaPrivacidad'));
const TerminosCondiciones = lazy(() => import('./pages/TerminosCondiciones'));
const EliminacionDatos = lazy(() => import('./pages/EliminacionDatos'));
const ExperienciaAyeka = lazy(() => import('./pages/ExperienciaAyeka'));
const ExperienciaRC = lazy(() => import('./pages/ExperienciaRC'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ContenidoPage = lazy(() => import('./pages/ContenidoPage'));
const SociosPage = lazy(() => import('./pages/SociosPage'));
const ElCirculoPage = lazy(() => import('./pages/ElCirculoPage'));
const GraciasSociosPage = lazy(() => import('./pages/GraciasSociosPage'));
const MiCuentaSocioPage = lazy(() => import('./pages/MiCuentaSocioPage'));
const RetiroApartadoPage = lazy(() => import('./pages/RetiroApartadoPage'));
const RetiroApartadoGraciasPage = lazy(() => import('./pages/RetiroApartadoGraciasPage'));
const ConferenciasPage = lazy(() => import('./pages/ConferenciasPage'));
const EventosPage = lazy(() => import('./pages/EventosPage'));
const EbookSuccess = lazy(() => import('./pages/EbookSuccess'));

// Loading spinner component — FIX 10: lucide-react instead of Font Awesome
const PageLoader = () => (
  <div className="min-h-screen bg-black flex items-center justify-center" data-testid="page-loader">
    <div className="text-center">
      <Loader2 className="animate-spin text-orange-500 w-8 h-8 mx-auto" />
      <p className="text-zinc-500 mt-4 text-sm">Cargando...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="protected-route-loader">
        <Loader2 className="animate-spin text-orange-500 w-8 h-8" />
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Admin-only route protection
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="admin-route-loader">
        <Loader2 className="animate-spin text-orange-500 w-8 h-8" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check if user is admin
  if (user.role !== 'admin' && !user.is_admin) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Promo code redirect → /curso/ayeka (oferta única: sin cupón; el código se descarta)
function PromoRedirect() {
  return <RedirectKeepQuery to="/curso/ayeka" utms={REDIRECT_UTMS.promoCode} />;
}

// Short marketing slug → canonical landing. Quita cupon/coupon, conserva utm_* y
// demás query + #hash, y pone las UTMs por defecto solo si no vino ninguna.
function RedirectKeepQuery({ to, utms }) {
  const { search, hash } = useLocation();
  const url = redirectUrlWithUtms(to, search, utms);
  const [pathname, query] = url.split('?');
  return <Navigate to={{ pathname, search: query ? `?${query}` : '', hash }} replace />;
}

// Router wrapper to handle Google Auth callback
function AppRouter() {
  const location = useLocation();
  
  // Plausible Analytics — track SPA page navigations
  useEffect(() => {
    window.plausible?.('pageview');
  }, [location.pathname]);

  // FCC Attribution — defensive re-init on every navigation (idempotent: locks attribution
  // on first call, no-op on subsequent calls). Catches edge cases like direct
  // navigation to inner pages.
  useEffect(() => {
    try { initAttributionTracker(); } catch { /* noop */ }
  }, [location.pathname]);

  // UTMs que llegan por navegación client-side (redirects internos, botón de /links):
  // el tracker solo lee la URL de entrada, así que se adoptan aquí, sin pisar nunca
  // una campaña que la sesión ya traiga.
  useEffect(() => {
    const utms = utmsFromSearch(location.search);
    if (Object.keys(utms).length === 0) return;
    try { adoptCampaignUtms(utms); } catch { /* noop */ }
  }, [location.search]);

  // FIX 5: Only intercept if it's a Google Auth callback (has access_token or id_token)
  const isGoogleCallback = location.hash?.includes('access_token=') ||
                            location.hash?.includes('id_token=') ||
                            (location.hash?.includes('session_id=') && location.pathname === '/auth/callback');

  if (isGoogleCallback) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }

  return (
    <ErrorBoundary>
      <PageTracker />
      
      <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicHomePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/entrar" element={<AuthLink />} />
        <Route path="/acceso" element={<TrialAccess />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/cursos" element={<PublicCoursesPage />} />
        
        {/* Application Funnel (for Wix redirect) */}
        <Route path="/aplicar" element={<ApplicationQuestionnaire />} />
        <Route path="/apply/interview" element={<InterviewBookingPage />} />
        <Route path="/interview" element={<InterviewBookingPage />} />
        
        {/* Public Course Landing Pages (for leads) */}
        <Route path="/links" element={<RedirectKeepQuery to="/curso/ayeka" utms={REDIRECT_UTMS.links} />} />
        <Route path="/curso/ayeka" element={<AyekaLandingPage />} />
        <Route path="/curso/ayeka/:mes" element={<AyekaMonthLanding />} />
        <Route path="/ayeka/bienvenida" element={<AyekaBienvenida />} />
        <Route path="/ayeka/trial" element={<AyekaTrialStart />} />
        <Route path="/ayeka-promo" element={<RedirectKeepQuery to="/curso/ayeka" utms={REDIRECT_UTMS.ayekaPromo} />} />
        <Route path="/ayeka/trial/bienvenida" element={<AyekaTrialWelcome />} />
        <Route path="/ayeka/continuar" element={<AyekaConvertir />} />
        <Route path="/ayeka/convertido" element={<AyekaConvertido />} />
        <Route path="/ayeka/comenzar" element={<AyekaComenzar />} />
        <Route path="/curso/mental" element={<MentalLandingPage />} />
        <Route path="/curso/ruta-critica" element={<RutaCriticaLandingPage />} />
        <Route path="/curso/ruta-critica/pago" element={<RutaCriticaPaymentPage />} />
        <Route path="/retiro" element={<RetiroLandingPage />} />

        {/* Legacy link redirects → canonical program landings */}
        <Route path="/cursos/ruta-critica" element={<Navigate to="/curso/ruta-critica" replace />} />
        <Route path="/programa/ruta-critica" element={<Navigate to="/curso/ruta-critica" replace />} />
        <Route path="/cursos/ayeka" element={<Navigate to="/curso/ayeka" replace />} />
        <Route path="/programa/ayeka" element={<Navigate to="/curso/ayeka" replace />} />
        <Route path="/cursos/new-year" element={<Navigate to="/curso/new-year" replace />} />
        <Route path="/programa/new-year" element={<Navigate to="/curso/new-year" replace />} />
        <Route path="/ayeka" element={<RedirectKeepQuery to="/curso/ayeka" utms={REDIRECT_UTMS.ayeka} />} />
        <Route path="/ruta-critica" element={<RedirectKeepQuery to="/curso/ruta-critica" utms={REDIRECT_UTMS.rutaCritica} />} />
        {/* Alias Growth/Community: /mi-cuenta 404 -> perfil; auth guard sigue en /dashboard/profile */}
        <Route path="/mi-cuenta" element={<RedirectKeepQuery to="/dashboard/profile" />} />
        <Route path="/mi-cuenta/*" element={<RedirectKeepQuery to="/dashboard/profile" />} />
        <Route path="/curso/circulo" element={<CirculoLandingPage />} />
        <Route path="/curso/new-year" element={<NewYearLandingPage />} />
        <Route path="/donar" element={<GuestDonate />} />
        <Route path="/aportar" element={<Navigate to="/donar" replace />} />
        
        {/* Referral Landing Page */}
        <Route path="/r/:code" element={<ReferralLandingPage />} />
        <Route path="/promo/:code" element={<PromoRedirect />} />
        
        {/* Ruta Critica Application Flow */}
        <Route path="/ruta-critica/aplicar" element={<RutaCriticaApplicationPage />} />
        
        {/* Public Blog Routes */}
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/media" element={<MediaLandingPage />} />
        <Route path="/contenido" element={<ContenidoPage />} />
        <Route path="/socios" element={<SociosPage />} />
        <Route path="/socios/gracias" element={<GraciasSociosPage />} />
        <Route path="/socios/mi-cuenta" element={<MiCuentaSocioPage />} />
        <Route path="/el-circulo" element={<ElCirculoPage />} />
        <Route path="/retiro/apartado" element={<RetiroApartadoPage />} />
        <Route path="/retiro/apartado/gracias" element={<RetiroApartadoGraciasPage />} />
        <Route path="/conferencias" element={<ConferenciasPage />} />
        <Route path="/eventos" element={<EventosPage />} />
        <Route path="/human-retreat" element={<Navigate to="/eventos" replace />} />
        <Route path="/human-1.0" element={<Navigate to="/eventos" replace />} />
        <Route path="/ebook/gracias" element={<EbookSuccess />} />
        <Route path="/politica-de-privacidad" element={<PoliticaPrivacidad />} />
        <Route path="/aviso-de-privacidad" element={<PoliticaPrivacidad />} />
        <Route path="/terminos-y-condiciones" element={<TerminosCondiciones />} />
        <Route path="/terminos" element={<TerminosCondiciones />} />
        <Route path="/eliminacion-de-datos" element={<EliminacionDatos />} />
        <Route path="/experiencia/ayeka" element={<ExperienciaAyeka />} />
        <Route path="/experiencia/ruta-critica" element={<ExperienciaRC />} />
      
        {/* Public Podcast Route */}
        <Route path="/podcast" element={<PodcastPage />} />

        {/* Public Payment Pages (used by Payment Links + Stripe redirects when not logged in) */}
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/subscription/success" element={<PaymentSuccess />} />
      
        {/* Protected Routes - Members Area */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <UserLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="cursos" element={<CoursesPage />} />
          {/* FIX 1: Specific routes BEFORE dynamic :courseId */}
          <Route path="course/ayeka" element={<AyekaPage />} />
          <Route path="course/circulo" element={<CirculoPage />} />
          <Route path="course/new-year-old-you" element={<NewYearCoursePage />} />
          <Route path="course/new-year-old-you/donar" element={<NewYearDonate />} />
          <Route path="course/new-year-old-you/:moduleKey/ejercicio" element={<NewYearExercise />} />
          <Route path="course/new-year-old-you/:moduleKey" element={<NewYearClassPage />} />
          <Route path="course/mental" element={<Navigate to="/curso/mental" replace />} />
          <Route path="course/:courseId" element={<RutaCriticaPage />} />
          <Route path="cursos/ayeka/leccion/:lessonNum" element={<AyekaLesson />} />
          {/* Defensive alias: protects old emails/links that may use "course" singular */}
          <Route path="course/ayeka/leccion/:lessonNum" element={<AyekaLesson />} />
          {/* FIX 7: Blog routes removed from dashboard — public routes handle all users */}
          <Route path="videos" element={<VideosPage />} />
          <Route path="podcast" element={<PodcastPage />} />
          <Route path="payment" element={<PaymentPage />} />
          <Route path="payment/:courseId" element={<PaymentPage />} />
          <Route path="payment/success" element={<PaymentSuccess />} />
          <Route path="payment/cancel" element={<PaymentCancel />} />
          <Route path="subscription/success" element={<PaymentSuccess />} />
          <Route path="questionnaire/:week" element={<WeeklyQuestionnaire />} />
          <Route path="pre-course-questionnaire" element={<PreCourseQuestionnaire />} />
          <Route path="booking" element={<BookingPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>

        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />

        {/* FIX 6: Custom 404 page instead of silent redirect */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
import PixelRouteTracker from './components/PixelRouteTracker';

function App() {
  return (
    <BrowserRouter>
      <PixelRouteTracker />
      <AuthProvider>
        <ProgressProvider>
          <AppRouter />
          <Toaster />
          <UpdatePrompt />
        </ProgressProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
